
import Vue, { PropType } from 'vue';
import { ApplicationWizardCard, ApplicationSpinner } from '../../../components/application-wizard';
import { SubmitBody } from '../../../components/submit-page';
import { ApplicationPdf } from '../../../components/application-pdf';
import { ApplicationValidationModal } from '../../../components/application-validation-modal';
import { RecreateSignatureModal } from '../../../components/recreate-signature-modal';
import { AutoFactory, HttpResponse, LoanApplication, LoanOfficer, OnlinePortalEmailApi, Permission, RecipientSignatureStatus } from '@sageworks/jpi';
import { ApplicationValidation, ApplicationFormPdfCssStyleUtils } from '../../../utils';
import moment from 'moment';
import { PromiseHelper } from '@sageworks/core-logic';
import { OnlinePortalRoutes } from '../../../OnlinePortalRoutes';
import { ApplicationValidationStage } from '../../../enums';
import { ToApplicationTemplateType } from '../../../utils/loan-app-type-helper/LoanAppTypeHelper';
import { ApplicationSubmitValidationErrors, ApplicationSubmitValidationResults } from '../../../utils/application-validation';
import { isApplicationLocked } from '../../../logic/loan-application.service';
import { UsableProducts } from '@sageworks/jpi';
import { isInFinalStatus as isApplicationInFinalStatus } from '../../../utils/application-final-status-helper/application-final-status-helper';
import { mapGetters } from 'vuex';

export default Vue.extend({
	name: 'MultiLoanApplicationSubmit',
	components: {
		ApplicationWizardCard,
		ApplicationPdf,
		ApplicationSpinner,
		ApplicationValidationModal,
		RecreateSignatureModal,
		SubmitBody
	},
	props: {
		applicationId: {
			type: String as PropType<string | null>,
			default: null
		}
	},
	data() {
		return {
			isInitializing: false,
			isSubmitting: false,
			isValidating: false,
			isSendingESignEmails: false,
			skipApplicationSubmissionStatusValidation: false,
			isPdfLoaded: false,
			submissionSuccessful: undefined as boolean | undefined,
			markReadyForBorrowerSuccessful: undefined as boolean | undefined,
			eSignEmailSendingSucceeded: undefined as boolean | undefined,
			validator: undefined as ApplicationValidation.MultiLoanApplicationValidator | undefined,
			validationErrors: undefined as ApplicationSubmitValidationErrors | undefined,
			ApplicationValidationStage: ApplicationValidationStage,
			isBypassingSignatures: false,
			isBypassingReview: false,
			isMarkReadyForBorrower: false
		};
	},
	computed: {
		...mapGetters('LoanApplicationMetadata', ['loanRequiresReview']),
		shouldMatchGeneralMessage(): boolean {
			return this.directStore.getters.InstitutionSettings.shouldMatchGeneralDocumentsPageMessage;
		},
		applicationIdAsNumber(): number | null {
			if (this.applicationId == null) {
				return null;
			}
			const id = Number.parseInt(this.applicationId, 10);
			if (Number.isNaN(id)) {
				return null;
			}
			return id;
		},
		applicationType(): LoanApplication.TypeEnum | null | undefined {
			return this.directStore.state.LoanApplicationData.loanApplication?.type;
		},
		currentUserIsLender(): boolean {
			return this.directStore.getters.User.isLender;
		},
		loading(): boolean {
			const hasMessage = this.directStore.getters.TermsAndConditions.termsAndConditionsMessageHtml != null;
			const hasLoadedBorrowerRecipients = this.directStore.getters.ESignModule.adobeRecipientStatuses != null;

			return this.isInitializing || !hasMessage || (this.customerCanUseEsign && !hasLoadedBorrowerRecipients);
		},
		submittedDate(): Date | null {
			return this.directStore.getters.LoanApplicationData.loanApplicationSubmittedDate;
		},
		submittedDateText(): string {
			const date = this.submittedDate;
			if (!date) {
				return '';
			}
			return moment(date).format('MM/DD/YYYY');
		},
		loanApplication(): LoanApplication | null {
			return this.directStore.state.LoanApplicationData.loanApplication;
		},
		logoUrl(): string {
			return this.directStore.getters.StylingSettings.logoUrl;
		},
		renderData() {
			return this.directStore.state.PDFPreviewModule.renderData ?? [];
		},
		loanOfficer(): LoanOfficer | null {
			return this.directStore.state.LoanApplicationData.loanOfficer;
		},
		adobeAgreementCreated(): boolean {
			return this.directStore.getters.ESignModule.adobeAgreementCreated;
		},
		allRecipientsHaveSignedAgreement(): boolean {
			return this.directStore.getters.ESignModule.allRecipientsHaveSignedAgreement;
		},
		customerCanUseEsign(): boolean {
			return this.directStore.state.ESignModule.customerCanUseEsign || false;
		},
		showEsignLines(): boolean {
			if (this.customerCanUseEsign) {
				return true;
			}

			const esignLinesSetting = this.directStore.state.InstitutionSettings.showEsignLinesSettings;
			const loanApplicationType = this.loanApplication?.type;
			return esignLinesSetting?.get(loanApplicationType!) ?? true;
		},
		isLocked(): boolean {
			const loanApplication = this.directStore.state.LoanApplicationData.loanApplication;
			return loanApplication && isApplicationLocked(loanApplication);
		},
		loadingText(): string {
			return !this.currentUserIsLender && this.isLocked ? 'Submission successful, returning to home' : '';
		},
		currentValidationStage(): ApplicationValidationStage {
			return this.validator?.currentStage ?? ApplicationValidationStage.NotValidating;
		},
		validationResults(): ApplicationSubmitValidationResults {
			return this.validator?.stepResults ?? {};
		},
		canSubmitApplicationWithoutESignatures(): boolean {
			return this.directStore.getters.LenderAuthorizationModule.canAccessFeature(Permission.ProductFeatureEnum.SubmitApplicationWithoutESignatures);
		},
		itemLabel(): string {
			if (this.hasCommunityLendingSubscription === undefined) {
				return '';
			}
			return this.hasCommunityLendingSubscription ? 'Request Form' : 'Application';
		},
		applicationStatus(): LoanApplication.ApplicationStatusEnum | null {
			return this.directStore.state.LoanApplicationData.loanApplication?.applicationStatus ?? null;
		},
		canSubmitBeforeLenderReview(): boolean {
			return this.directStore.getters.LenderAuthorizationModule.canAccessFeature(Permission.ProductFeatureEnum.SubmitApplicationBeforeLenderReview);
		},
		canLenderMarkReadyForLenderReview(): boolean {
			return this.directStore.getters.LenderAuthorizationModule.canAccessFeature(Permission.ProductFeatureEnum.MarkApplicationReadyForLenderReview);
		},
		canBypassValidationOnReadyForBorrowerSubmit(): boolean {
			return this.directStore.getters.LenderAuthorizationModule.canAccessFeature(
				Permission.ProductFeatureEnum.AllowBypassValidationOnReadyForBorrowerSubmit
			);
		},
		canSubmitApplicationInBorrowerReview(): boolean {
			return this.currentUserIsLender
				? this.directStore.getters.LenderAuthorizationModule.canAccessFeature(Permission.ProductFeatureEnum.SubmitApplicationInBorrowerReview)
				: true;
		},
		isInFinalStatus(): boolean {
			return isApplicationInFinalStatus(this.applicationStatus, this.canSubmitApplicationInBorrowerReview);
		},

		title(): string {
			if (!this.isInFinalStatus && !this.canSubmitBeforeLenderReview && this.loanRequiresReview) {
				return 'Review';
			}
			return 'Submit ' + this.itemLabel;
		},
		defaultPreSubmitMessage(): string {
			return 'Thank you for completing your ' + this.itemLabel + '! Once submitted, we will be in contact with next steps.';
		},
		defaultPreMarkForReviewMessage(): string {
			// eslint-disable-next-line max-len
			return 'Thank you for completing your request! Notify a lender by clicking on “Mark Ready for Review”. Once we review your request, we will be in contact with next steps.';
		},
		hasCommunityLendingSubscription(): boolean | undefined {
			return this.directStore.state.UsableProducts.usableProducts?.has('abrigoCommunityLending' as UsableProducts.ProductsEnum);
		},
		preSubmitMessage(): string {
			// If the setting to use general is not enabled, and there is a custom message, use the custom message for this application type
			if (!this.shouldMatchGeneralMessage && this.directStore.state.UserCreatedMessages.SubmitApplicationPreMessageHtml?.trim().length !== 0) {
				// If result is undefined or null, use the default message
				return this.directStore.state.UserCreatedMessages.SubmitApplicationPreMessageHtml ?? this.defaultPreSubmitMessage;
			}
			// Use the general message but if that is null use the default message
			if (this.shouldMatchGeneralMessage && this.directStore.state.InstitutionSettings.submitApplicationPreMessage.message?.trim().length !== 0) {
				// If result is undefined or null, use the default message
				return this.directStore.state.InstitutionSettings.submitApplicationPreMessage.message ?? this.defaultPreSubmitMessage;
			}
			return this.defaultPreSubmitMessage;
		},
		// Default post submit message is empty for now but easy to change later if we need to.
		defaultPostSubmitMessage(): string {
			return '';
		},
		postSubmitMessage(): string {
			// If the setting to use general is not enabled, and there is a custom message, use the custom message for this application type
			if (!this.shouldMatchGeneralMessage && this.directStore.state.UserCreatedMessages.SubmitApplicationPostMessageHtml?.trim().length !== 0) {
				return this.directStore.state.UserCreatedMessages.SubmitApplicationPostMessageHtml ?? this.defaultPostSubmitMessage;
			}
			// Use the general message but if that is null there is no message
			if (this.shouldMatchGeneralMessage && this.directStore.state.InstitutionSettings.submitApplicationPostMessage.message?.trim().length !== 0) {
				return this.directStore.state.InstitutionSettings.submitApplicationPostMessage.message ?? this.defaultPostSubmitMessage;
			}

			return this.defaultPostSubmitMessage;
		}
	},
	async mounted() {
		this.validator = new ApplicationValidation.MultiLoanApplicationValidator(this.directStore, this.applicationIdAsNumber ?? undefined);
		this.loadInitialData();
	},

	methods: {
		async loadInitialData() {
			this.isInitializing = true;
			this.isPdfLoaded = false;

			try {
				// Load loan application data
				await this.directStore.dispatch.LoanApplicationData.loadLoanApplication({
					loanApplicationId: this.applicationIdAsNumber as number
				});

				if (!this.applicationType || !this.applicationIdAsNumber) {
					throw new Error('loan application was not loaded');
				}

				// Load PDF data but do not wait for it, so we don't prevent the user from submitting
				// 	before the PDF is loaded
				this.loadPdfData();

				// Get the indicator of if we should use the general messages or the custom messages - if the custom messages, load them...
				await this.directStore.dispatch.InstitutionSettings.fetchShouldMatchGeneralDocumentsPageMessage({ applicationType: this.applicationType });
				if (!this.shouldMatchGeneralMessage) {
					await this.directStore.dispatch.UserCreatedMessages.fetchSubmitApplicationPreMessage(ToApplicationTemplateType(this.applicationType));
					await this.directStore.dispatch.UserCreatedMessages.fetchSubmitApplicationPostMessage(ToApplicationTemplateType(this.applicationType));
				}

				const signatureType = await this.directStore.dispatch.ESignModule.fetchSignatureType({ applicationType: this.applicationType });

				// Load submission data and settings
				await Promise.all([
					// Terms and conditions
					this.directStore.getters.TermsAndConditions.termsAndConditionsMessageHtml == null
						? this.directStore.dispatch.TermsAndConditions.fetchTermsAndConditionsMessage(ToApplicationTemplateType(this.applicationType))
						: Promise.resolve(),

					// Auto lock setting
					this.directStore.dispatch.InstitutionSettings.fetchApplicationAutoLockSettings(),

					// esign lines setting
					this.directStore.dispatch.InstitutionSettings.fetchShowEsignLinesSettings(),

					// e-sign setting
					this.directStore.dispatch.ESignModule.getUserCanUseEsign({ applicationType: this.applicationType }),

					// phone auth setting
					this.directStore.dispatch.ESignModule.getUserPhoneAuthEnabled({
						applicationType: this.applicationType,
						signatureType: signatureType
					}),

					// metadata and proposed products
					this.directStore.dispatch.LoanApplicationMetadata.loadMetadata({
						loanApplicationId: this.applicationIdAsNumber,
						ignoreFormioProperties: true
					}),
					this.directStore.dispatch.LenderAuthorizationModule.loadUserPermissions(),
					this.directStore.dispatch.UsableProducts.fetchUsableProducts()
				]);
			} catch (e) {
				this.showToast('Loading Failure', 'An error occurred while loading the form for submission.');
			}

			this.isInitializing = false;
		},
		async loadPdfData() {
			await Promise.all([
				this.directStore.dispatch.LoanApplicationData.fetchLoanOfficer(),
				this.directStore.dispatch.PDFPreviewModule.loadPdfData(this.applicationIdAsNumber as number)
			]);

			this.isPdfLoaded = true;
		},
		async submitApplication() {
			const validForSubmit = await this.validateForSubmit();
			if (!validForSubmit) {
				return;
			}
			this.isSubmitting = true;

			try {
				await this.directStore.dispatch.LoanApplicationData.submitApplication();
				await this.saveApplicationPdfToDocLibrary();

				if (!this.currentUserIsLender && this.isLocked) {
					await new Promise(resolve => setTimeout(resolve, 1000));
					this.$router.push(OnlinePortalRoutes.Home.path);
				}
				this.submissionSuccessful = true;
			} catch (error) {
				this.submissionSuccessful = false;
				this.isBypassingSignatures = false;
				this.validationErrors = {
					messages: [
						{
							title: 'Unable to Submit Application',
							message:
								'An error occurred during submission. Please contact your institution if you are unable to complete the submission process.'
						}
					],
					productsNotValid: []
				};
			}
			this.isSubmitting = false;
		},
		async markReadyForReview() {
			this.isBypassingSignatures = true;
			const validForReview = await this.validateForSubmit();
			if (!validForReview) {
				return;
			}
			this.isSubmitting = true;

			try {
				await this.directStore.dispatch.LoanApplicationData.updateApplicationStatus({
					applicationStatus: LoanApplication.ApplicationStatusEnum.LenderReview
				});

				const emailService = AutoFactory.get(OnlinePortalEmailApi);
				emailService.sendHardcodedEmail('applicationReadyForReview', this.applicationIdAsNumber as number);

				this.submissionSuccessful = true;
			} catch (error) {
				this.submissionSuccessful = false;
				this.isBypassingSignatures = false;
				this.validationErrors = {
					messages: [
						{
							title: 'Unable to Submit for Review',
							message:
								'An error occurred during submission. Please contact your institution if you are unable to complete the submission process.'
						}
					],
					productsNotValid: []
				};
			}
			this.isSubmitting = false;
		},
		async saveApplicationPdfToDocLibrary() {
			if (this.applicationType === null) {
				return;
			}
			await this.directStore.dispatch.InstitutionSettings.fetchAutoSavePdfToDocLibOnSubmission({ applicationType: this.applicationType });
			await this.directStore.dispatch.InstitutionSettings.fetchApplicationSavePdfType({ applicationType: this.applicationType });
			const autoSavePdfToDocLib = this.directStore.getters.InstitutionSettings.autoSavePdfToDocLibOnSubmission;
			const isEsignEnabledButSignaturesAreIgnored = this.customerCanUseEsign && this.isBypassingSignatures;
			const savePdfType = this.directStore.getters.InstitutionSettings.applicationSavePdfType;
			const isLenderSavePdfType = savePdfType === 'lender';

			if (autoSavePdfToDocLib || isEsignEnabledButSignaturesAreIgnored) {
				const html = await this.generateHtmlForPdf();
				await this.directStore.dispatch.LoanApplicationData.saveApplicationPdfToDocumentLibrary({ html, createFromLenderUrl: isLenderSavePdfType });
			}
		},
		async recreateSignaturePackage() {
			this.sendEmailsForSignatures(true);
		},
		// eslint-disable-next-line max-lines-per-function
		async sendEmailsForSignatures(recreateSignaturePackage: boolean = false) {
			try {
				if (this.recipientsAreMissingEmails()) {
					this.eSignEmailSendingSucceeded = false;
					this.validationErrors = {
						messages: [
							{
								title: 'Missing Email Address',
								message: 'Email address is required to send for signature.'
							}
						],
						productsNotValid: []
					};
					this.isSendingESignEmails = false;
					return;
				}

				if (this.directStore.state.ESignModule.customerHasPhoneAuthEnabled && this.recipientsAreMissingPhoneNumbers()) {
					this.eSignEmailSendingSucceeded = false;
					this.validationErrors = {
						messages: [
							{
								title: 'Missing Phone Number',
								message: 'Phone Number is required to send for signature.'
							}
						],
						productsNotValid: []
					};
					this.isSendingESignEmails = false;
					return;
				}

				// Lenders should be able to recreate signatures even after the application has already been submitted
				this.skipApplicationSubmissionStatusValidation = this.currentUserIsLender;

				// Opens the application validation modal, which will remain open until the end of this method/when the user closes it
				const validForSubmit = await this.validateForSubmit();
				if (!validForSubmit) {
					return;
				}

				this.isSendingESignEmails = true;
				const html = await this.generateHtmlForPdf();

				let response;
				if (!recreateSignaturePackage) {
					response = await this.directStore.dispatch.ESignModule.sendApplicationEmailsForSignatures({
						applicationId: (this.applicationIdAsNumber as number) as number,
						html
					});
				} else {
					response = await this.directStore.dispatch.ESignModule.resendApplicationEmailsForSignatures({
						applicationId: (this.applicationIdAsNumber as number) as number,
						html
					});
				}

				// Check for specific phone number error
				if (response?.error && response.errorCode === 'PHONE_NUMBER_INVALID') {
					this.eSignEmailSendingSucceeded = false;
					// Show different messages based on user type
					const phoneErrorMessage = this.currentUserIsLender
						? response.userMessage // Full message with Abrigo Support contact info for lenders
						: 'The Phone Number provided for the applicant(s) is invalid. Please review and correct, and then try again.';

					this.validationErrors = {
						messages: [
							{
								title: 'Invalid Phone Number',
								message: phoneErrorMessage
							}
						],
						productsNotValid: []
					};
					this.isSendingESignEmails = false;
					return; // Exit early, don't mark as succeeded
				}

				// Check for other error responses from backend (e.g., private message errors)
				if (response?.error && response.userMessage) {
					this.eSignEmailSendingSucceeded = false;
					this.validationErrors = {
						messages: [
							{
								title: 'E-Signature Error',
								message: response.userMessage
							}
						],
						productsNotValid: []
					};
					this.isSendingESignEmails = false;
					return; // Exit early, don't mark as succeeded
				}

				await this.directStore.dispatch.ESignModule.fetchLoanApplicationRecipientStatuses({
					applicationId: this.applicationIdAsNumber as number
				});

				this.eSignEmailSendingSucceeded = true;
			} catch (error) {
				// eslint-disable-next-line max-len
				var message = 'We encountered an issue sending your application for e-signature; please try again or contact your institution for assistance.';
				var errorMessage = '';
				if (error instanceof HttpResponse) errorMessage = error.response.message;
				// ensure that this matches the error message located in AdobeSignAuthenticationService
				if (errorMessage.includes('The refresh token request with Adobe failed') && this.currentUserIsLender) {
					message =
						// eslint-disable-next-line max-len
						'Adobe Sign token expired. In Sageworks My Account settings, disconnect and reconnect your Adobe Sign account, then retry. For further help, contact your administrator or Abrigo Support.';
				}
				this.eSignEmailSendingSucceeded = false;
				this.validationErrors = {
					messages: [
						{
							title: 'Unable to Send Emails',
							message: message
						}
					],
					productsNotValid: []
				};
			}

			this.isSendingESignEmails = false;
		},
		async readyForBorrowerToSubmit() {
			if (this.directStore.state.LoanApplicationData.loanApplication?.applicationStatus !== LoanApplication.ApplicationStatusEnum.LenderReview) {
				return;
			}

			// Mode is applied inside runValidation after the modal @show reset
			if (!this.canBypassValidationOnReadyForBorrowerSubmit) {
				const isValid = await this.runValidation(true, true, true, true);
				if (!isValid) {
					return;
				}
			}

			this.isValidating = true;

			try {
				await this.directStore.dispatch.LoanApplicationData.updateApplicationStatus({
					applicationStatus: LoanApplication.ApplicationStatusEnum.ReadyForBorrowerSubmission
				});
				await this.sendBorrowerEmail();
				this.markReadyForBorrowerSuccessful = true;
			} catch (error) {
				this.markReadyForBorrowerSuccessful = false;
				this.validationErrors = {
					messages: [
						{
							title: 'Unable to Mark Ready',
							message: 'An error occurred while marking the application ready for borrower submission.'
						}
					],
					productsNotValid: []
				};
			} finally {
				this.isValidating = false;
			}
		},
		async waitIfValidationFailed(results: ApplicationSubmitValidationResults): Promise<void> {
			if (Object.values(results ?? {}).some(item => item === false)) {
				await new Promise(f => setTimeout(f, 2000));
			}
		},
		async runValidation(
			skipSubmitStatusValidation: boolean = false,
			skipTermsAndConditionsValidation: boolean = false,
			skipDocumentValidation: boolean = false,
			isMarkReadyForBorrower: boolean = false
		): Promise<boolean> {
			this.$root.$bvModal.show('application-validation-process-modal');
			// @show resets validation data; set session mode after that reset
			this.isMarkReadyForBorrower = isMarkReadyForBorrower;
			this.validationErrors = undefined;
			this.isValidating = true;

			if (!this.validator) {
				throw new Error('validator was not properly set up');
			}

			const validationErrors = await this.validator.validate(skipSubmitStatusValidation, skipTermsAndConditionsValidation, skipDocumentValidation);

			await this.waitIfValidationFailed(this.validationResults);

			this.validationErrors = validationErrors;
			this.isValidating = false;

			return this.validationErrors?.messages.length === 0;
		},
		async generateHtmlForPdf() {
			// Need to wait to ensure all the data is loaded for the pdf, since we do not wait for the PDF to fully load
			// before allowing the user to submit the app
			await PromiseHelper.waitForConditionToBeMet(() => this.isPdfLoaded);

			// Wait one tick in order to allow the image logo to show in the HTML element
			await this.$nextTick();

			const htmlContent = (this.$refs['pdf'] as Vue).$el?.innerHTML || '';
			const stylesContent = ApplicationFormPdfCssStyleUtils.retrieveApplicationFormPdfStyles();
			return '<html><head><meta charset="utf-8"/>' + stylesContent + '</head><body>' + htmlContent + '</body></html>';
		},
		async showRecreateSignatureModal() {
			this.$root.$bvModal.show('recreate-signature-modal');
		},
		async sendBorrowerEmail() {
			await this.directStore.dispatch.LoanApplicationData.sendEmailLoanReadyForBorrowerSubmission({
				loanApplicationId: this.applicationIdAsNumber
			});
		},
		async validateForSubmit(): Promise<boolean> {
			return this.runValidation(
				this.skipApplicationSubmissionStatusValidation,
				!this.currentUserIsLender && this.loanRequiresReview && !this.isInFinalStatus
			);
		},
		resetValidationData(): void {
			this.isSubmitting = false;
			this.isValidating = false;
			this.isSendingESignEmails = false;
			this.skipApplicationSubmissionStatusValidation = false;
			this.submissionSuccessful = undefined;
			this.markReadyForBorrowerSuccessful = undefined;
			this.eSignEmailSendingSucceeded = undefined;
			this.isMarkReadyForBorrower = false;
			this.validationErrors = undefined;
		},
		recipientsAreMissingEmails(): boolean {
			let recipientStatuses = this.directStore.getters.ESignModule.adobeRecipientStatuses;
			let invalidBorrowers = recipientStatuses.filter((r: RecipientSignatureStatus) => {
				return r.email === null || r.email === undefined || r.email === '';
			});
			return invalidBorrowers.length > 0;
		},
		recipientsAreMissingPhoneNumbers(): boolean {
			let recipientStatuses = this.directStore.getters.ESignModule.adobeRecipientStatuses;
			let invalidBorrowers = recipientStatuses.filter((r: RecipientSignatureStatus) => {
				return r.phone === null || r.phone === undefined || r.phone === '';
			});
			return invalidBorrowers.length > 0;
		},
		showToast(title: string, message: string) {
			this.$root.$bvToast.toast(message, {
				title: title,
				solid: true,
				autoHideDelay: 3000,
				toaster: 'b-toaster-bottom-right'
			});
		},
		submitApplicationWithoutESignatures() {
			this.isBypassingSignatures = true;
			this.submitApplication();
		},
		submitApplicationBypassingReview() {
			this.isBypassingReview = true;
			this.submitApplicationWithoutESignatures();
		}
	}
});
